"use client";

import Image from "next/image";
import AnimateOnScroll from "./AnimateOnScroll";
import TeamAvatarStack from "./TeamAvatarStack";
import WisdomTrigger from "../easter-eggs/WisdomTrigger";
import { teamMembers } from "@/lib/teamMembers";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";

const founder = teamMembers.find((m) => m.slug === "Alexander");

const founderSchema = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Alex Rodriguez",
  jobTitle: "Founder & Counsellor",
  description: founder?.description,
  image: new URL("/images/v3/about/alex-r.webp", SITE_URL).href,
  url: new URL("/teams/alexander", SITE_URL).href,
  ...(founder?.linkedin ? { sameAs: [founder.linkedin] } : {}),
  ...(founder?.email ? { email: `mailto:${founder.email}` } : {}),
  worksFor: {
    "@type": "Organization",
    name: ORGANIZATION_NAME,
    url: SITE_URL,
  },
};

const AboutUs = () => {
  return (
    <section
      className="w-full bg-[#F7FAFF] px-4 py-12 sm:px-6 sm:py-16 md:px-12 md:py-20 lg:px-24"
      aria-labelledby="about-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(founderSchema).replace(/</g, "\\u003c"),
        }}
      />
      <div className="mx-auto max-w-[1524px]">
        {/* ── TOP HEADER SECTION ── */}
        <div className="flex flex-col items-start justify-between gap-6 md:gap-8 lg:flex-row lg:items-center">
          {/* Left Title */}
          <div className="w-full lg:max-w-[550px]">
            <AnimateOnScroll>
              <h2 id="about-heading" className="heading text-[#02386D]">
                Your Path,
                <br />
                Our Purpose.
              </h2>
            </AnimateOnScroll>
          </div>

          {/* Right Social Proof */}
          <div className="flex w-full flex-col items-start gap-2 md:items-end md:justify-end lg:max-w-[460px]">
            <p className="mt-0.5 text-right font-onest text-[18px] font-medium text-black sm:text-[22px] md:text-[26px]">
              Hundreds of success stories <br /> and counting
            </p>
            <p className="w-full font-onest text-[13px] font-light leading-normal text-black sm:text-[14px] md:w-[90%] md:text-right">
              A caring team of counsellors, coaches, and allied health <br />
              professionals here to help you move toward your best self.
            </p>
            <div className="mt-1 flex items-center gap-2 sm:gap-3">
              <TeamAvatarStack />
            </div>
          </div>
        </div>

        {/* ── MIDDLE PARAGRAPH SECTION ── */}
        <div className="mt-10 flex flex-col gap-4 border-t border-black/10 pt-8 sm:mt-12 sm:gap-5 sm:pt-10 md:mt-16 md:gap-6 md:pt-12 lg:flex-row lg:gap-[100px]">
          <span className="w-24 shrink-0 font-onest text-[14px] font-bold uppercase tracking-wider text-[#02386D] sm:text-[16px]">
            About
          </span>
          <p className="font-onest text-[16px] font-light leading-relaxed text-black/80 sm:text-[18px] md:text-[20px] lg:text-[22px]">
            We are a supportive wellbeing practice built around one simple
            belief: people grow best when they feel heard, understood, and
            guided with care. Through professional counselling, life coaching,
            psychology, and occupational therapy, our team helps you work
            through life&apos;s challenges, build confidence, and take practical
            steps toward becoming the best version of yourself.
          </p>
        </div>

        {/* ── FOUNDER'S STORY SECTION ── */}
        <div className="mt-12 grid grid-cols-1 items-center gap-8 sm:mt-16 md:mt-20 md:gap-10 lg:mt-24 lg:grid-cols-3 lg:gap-20">
          {/* Left Side: Creative Image Frame */}
          <WisdomTrigger className="col-span-1 mx-auto max-w-[400px] select-none lg:mx-0 lg:max-w-none">
            <Image
              src="/images/v3/about/alex-r.webp"
              alt="Alex Rodriguez"
              height={750}
              width={622}
              className="h-full w-full object-contain"
              quality={100}
            />
          </WisdomTrigger>

          {/* Right Side: Copy Details */}
          <div className="col-span-1 flex flex-col gap-4 md:gap-6 lg:col-span-2">
            <div className="flex flex-col gap-1 sm:gap-2">
              <span className="font-crimsonText text-[20px] font-bold text-[#006DFF] sm:text-[22px] md:text-[24px]">
                Founder&apos;s Story
              </span>
              <h3 className="heading text-[#02386D]">Alex Rodriguez</h3>
            </div>

            <div className="flex flex-col gap-3 font-onest text-[14px] font-light leading-relaxed text-black/70 sm:gap-4 sm:text-[15px] md:text-[16px]">
              <p>
                Let&apos;s be real, life can have a way of persistently knocking
                us around, frustrating our ambitions and leaving us feeling
                stuck with no visible way forward. After a while, it can become
                too much. You&apos;re left asking yourself, why me? When will
                this end? What&apos;s the point of it all? But, there IS a way
                forward — and we will help you find your path.
              </p>
              <p>
                My own journey has been fraught with bouts of depression and a
                nervous breakdown, pushing me to the brink. This was the
                catalyst for my intense self-exploration and discovery. I
                stretched the boundaries of my mind, body, and spirit to
                reconnect with myself and reclaim my power.
              </p>
              <p>
                I&apos;ve been in the trenches, and now, I want to help you
                navigate yours.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
